import styled from '@emotion/styled';
import { tokens, Button, Text, Loader, Link } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';
import { memo, useEffect, useState } from 'react';
import {
  getSortingOptionsTranslationKey,
  SORTING_OPTIONS,
} from '@hotjar/recordings/src/constants/sorting-options';
import { ZINDEX } from '@hotjar/webapp/constants/z-index';

import type { PartialRecording } from '@hotjar/recordings/src/store/player/types';

import {
  useSliderVisibility,
  DEFAULT_TIMEOUT_MS,
} from '@hotjar/recordings/src/features/RecordingsPage/hooks/useSliderVisibility';
import { PlaylistItem } from './PlaylistItem';

const PLAYLIST_WIDTH_PX = 290;

/**
 * We're animating 2x the container width to half to simulate the behavior
 * on setting the width to 0 on the SidePanel, which is Left -> Right, while
 * here it's Right -> Left.
 */
const SliderContainer = styled.div<{ open: boolean }>`
  width: ${({ open }) => (open ? PLAYLIST_WIDTH_PX * 2 : PLAYLIST_WIDTH_PX)}px;
  transition: width ease-in-out ${DEFAULT_TIMEOUT_MS}ms;

  margin-left: -${PLAYLIST_WIDTH_PX}px;
  display: flex;
  justify-content: end;
  flex-shrink: 0;
`;

const Container = styled.div`
  user-select: text;
  display: flex;
  flex-direction: column;
  z-index: 10;
  background: ${tokens.color.ui_background};
  width: ${PLAYLIST_WIDTH_PX}px;
  height: 100%;
  border-right: 1px solid ${tokens.color.ui_border};
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;

  padding: ${tokens.spacer.x0pt5};
`;

const Heading = styled(Text)`
  padding: ${tokens.spacer.x1pt5};
  white-space: pre;
  ${tokens.text('body1')}
`;

const Subheading = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  white-space: pre;
  padding-left: ${tokens.spacer.x2};
  margin-bottom: ${tokens.spacer.x1pt5};
  ${tokens.text('body2')}

  padding-top: ${tokens.spacer.x1pt5};
`;

const List = styled.ul`
  border-top: 1px solid ${tokens.color.ui_border};
  list-style-type: none;
  padding: ${tokens.spacer.x2};
  flex: 1;
  overflow: auto;
  position: relative;
`;

const LoadMoreButton = styled(Button)`
  margin-bottom: ${tokens.spacer.x2};
`;

const SkipLinkContainer = styled(Link)<{ offsetX: string; offsetY: string }>`
  background: ${tokens.color.ui_background};
  border-radius: ${tokens.radius.m};
  padding: ${tokens.spacer.x1pt5};
  position: absolute;
  left: ${({ offsetX }) => offsetX};
  top: ${({ offsetY }) => offsetY};
  /* Increasing the z-index so that skip link shows up on top of the container that we want to skip */
  z-index: ${ZINDEX.APP_CONTENT};

  &:not(:focus):not(:active) {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    white-space: nowrap;
    width: 1px;
  }
`;

const focusOnElement = (targetSelector: string): void => {
  const targetElement = document.querySelector(targetSelector) as HTMLElement;

  if (targetElement && targetElement.tabIndex > -1) {
    targetElement.focus();
  } else {
    throw new Error("The provided skip link target selector doesn't exists!");
  }
};

interface SkipLinkProps {
  skipTarget: string;
  children: string;
  offsetX?: string;
  offsetY?: string;
}

// TODO: Replace with Hotsauce component when CHAP-1821 is done
const SkipLink = ({
  skipTarget,
  children,
  offsetX = '0',
  offsetY = '0',
}: SkipLinkProps): JSX.Element | null => {
  const [canSkipToTargetElement, setCanSkipToTargetElement] = useState(true);

  useEffect(() => {
    const targetElement = document.querySelector(skipTarget) as HTMLElement;

    if (!targetElement) {
      setCanSkipToTargetElement(false);
      // eslint-disable-next-line no-console
      console.error('The provided skip link target does not exists!');
      return;
    }

    if (targetElement?.tabIndex < 0) {
      setCanSkipToTargetElement(false);
      // eslint-disable-next-line no-console
      console.error('The provided skip link target is not focusable!');
    }
  }, [skipTarget]);

  if (!canSkipToTargetElement) {
    return null;
  }

  return (
    // Provide an empty `href` so that element can be focused with TAB
    <SkipLinkContainer
      onClick={() => focusOnElement(skipTarget)}
      kind="standalone"
      href=""
      offsetX={offsetX}
      offsetY={offsetY}
    >
      {children}
    </SkipLinkContainer>
  );
};

const isRecordingWatched = (
  recording: PartialRecording,
  watchedRecordingsIdsList: number[],
): boolean => watchedRecordingsIdsList.includes(recording.id);

type PlaylistParams = {
  recordings: PartialRecording[];
  activeRecrodingId: number | null;
  isPlaying: boolean;
  playlistOpened: boolean;
  hasMoreRecordings: boolean;
  watchedRecordingsIdsList: number[];
  sortBy: string;
  isPlayingFunnels?: boolean;
  goToRecording: (id: number) => void;
  fetchNextRecordingsPage: () => void;
};

const UnMemoPlaylist = ({
  recordings,
  activeRecrodingId,
  isPlaying,
  playlistOpened,
  hasMoreRecordings,
  watchedRecordingsIdsList,
  sortBy,
  isPlayingFunnels,
  goToRecording,
  fetchNextRecordingsPage,
}: PlaylistParams): JSX.Element | null => {
  const { t } = useTranslation();
  const sortTranslationKey = SORTING_OPTIONS.find((option) => sortBy.includes(option));

  const { visible, open } = useSliderVisibility(playlistOpened);
  if (!visible) {
    return null;
  }

  return (
    <SliderContainer open={open} className="playlist">
      <Container>
        <Header>
          <Heading>
            {isPlayingFunnels
              ? t('common:recordings.player.playlist.title-for-funnels')
              : t('common:recordings.player.playlist.title')}
          </Heading>
        </Header>
        {sortTranslationKey ? (
          <Subheading>
            <Text>{t('common:recordings.player.playlist.sort-by')} </Text>
            <Text textType="subtitle2">
              {t(
                `common:recordings.sorting.${getSortingOptionsTranslationKey(sortTranslationKey)}`,
              )}
            </Text>
          </Subheading>
        ) : null}
        <List data-testid="playlist">
          <SkipLink
            skipTarget="#play-pause-replay-button button"
            offsetX={tokens.spacer.x2}
            offsetY={tokens.spacer.x2}
          >
            {t('common:recordings.player.playlist.skip-link')}
          </SkipLink>
          {recordings.length ? (
            <>
              {recordings.map((recording, index) => (
                <li key={recording.id}>
                  <PlaylistItem
                    recording={recording}
                    index={index}
                    isActive={recording.id === activeRecrodingId}
                    isPlaying={recording.id === activeRecrodingId && isPlaying}
                    isWatched={isRecordingWatched(recording, watchedRecordingsIdsList)}
                    onClick={goToRecording}
                  />
                </li>
              ))}
              {hasMoreRecordings ? (
                <LoadMoreButton
                  kind="ghost"
                  size="s"
                  icon="plus"
                  onClick={fetchNextRecordingsPage}
                  data-testid="load-more-button"
                >
                  {t('common:recordings.player.playlist.load-more')}
                </LoadMoreButton>
              ) : null}
            </>
          ) : (
            <Loader />
          )}
        </List>
      </Container>
    </SliderContainer>
  );
};

export const Playlist = memo(UnMemoPlaylist);
